import { useCallback, useEffect, useState } from "react";
import AdminService from "../../../../../service/AdminService";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import { redirectIfUnauthorized } from "../../../utils/adminUtils";

// Mesmos endpoints da ListUsers clássica, um por plano
const FETCHERS = {
  areamanager: (service, token) => service.getAreaManagers(token),
  businesspartner: (service, token) => service.getBusinessPartners(token),
  specialcustomer: (service, token) => service.getSpecialCustomers(token),
  salepromoter: (service, token) => service.getSalePromoter(token),
  sellers: (service, token) => service.getSellers(token),
};

// Usuários cancelados têm os dados pessoais ocultados, como no admin clássico
const normalizeUser = (user) =>
  user.cancelled
    ? {
        ...user,
        username: "Cancelled",
        firstname: "",
        surname: "",
        phonenumber: "",
        email: "",
        referall: "",
      }
    : user;

const useAdminUsers = (plan) => {
  const { showErrorToast, showSuccessToast, showInfoToast } = useToastContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [updatingUsername, setUpdatingUsername] = useState(null);

  const fetchUsers = useCallback(
    async (isCancelled = () => false) => {
      const fetcher = FETCHERS[plan];
      if (!fetcher) return;

      setLoading(true);
      try {
        const res = await fetcher(new AdminService(), localStorage.getItem("__extraconomy_token"));
        if (isCancelled()) return;

        if (res.messageCode !== 200001) {
          setLoadError(true);
          showErrorToast({ code: res.messageCode });
          return;
        }

        setLoadError(false);
        setUsers((res.result?.userListJSONs || []).map(normalizeUser));
      } catch (e) {
        if (isCancelled() || redirectIfUnauthorized(e)) return;
        console.log(e);
        setLoadError(true);
        showErrorToast({ code: 500000 });
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plan],
  );

  useEffect(() => {
    let cancelled = false;
    setUsers([]);
    fetchUsers(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchUsers]);

  const setBlocked = async (username, block) => {
    setUpdatingUsername(username);
    try {
      const res = await new AdminService().postBlockOrUnblockUser(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
        username,
        !!block,
      );

      if (res.messageCode !== 200000) {
        if (res.messageCode === 500000) console.log(res);
        showErrorToast({ code: res.messageCode });
        return;
      }

      if (block) showInfoToast({ summary: "info", detail: "userBlockedSuccessfully" });
      else showSuccessToast({ detail: "userUnblockedSuccessfully" });

      // Atualiza a linha na hora e recarrega a lista para ficar igual ao servidor
      setUsers((prev) =>
        prev.map((user) => (user.username === username ? { ...user, blocked: !!block } : user)),
      );
      fetchUsers();
    } catch (e) {
      if (redirectIfUnauthorized(e)) return;
      console.log(e);
      showErrorToast({ code: 500000 });
    } finally {
      setUpdatingUsername(null);
    }
  };

  return { users, loading, loadError, updatingUsername, setBlocked, reload: () => fetchUsers() };
};

export default useAdminUsers;
